<template>
  <BaseBanner
    :class="[
      $b({ colour: primaryColour, inlineCopy, hasFeatures }),
      { 'has-hover': hasHover },
    ]"
    v-bind="$attrs"
  >
    <div :class="$b('content', { hasFeatures })">
      <BaseHeading
        v-if="heading.text"
        :class="
          $b('heading', { wrap: isSmallHeading, gradient: headingGradient })
        "
        :size="inlineCopy ? 'h1' : headingSize"
        :tag="heading.tag || 'h2'"
        :style="{ maxWidth: maxHeadingWidth ? `${maxHeadingWidth}px` : null }"
      >
        <!-- eslint-disable-next-line vue/no-v-html -->
        <span v-html="parseText($tr(heading.text))" />
      </BaseHeading>

      <BaseHeading
        v-if="copy && inlineCopy"
        :class="$b('inline-copy')"
        size="h1"
        tag="h4"
      >
        {{ copy }}
      </BaseHeading>

      <div
        v-else-if="resolvedCopy"
        :class="
          $b('copy-wrapper', {
            halfWidth: halfWidthCopy,
            fullWidth: fullWidthCopy,
          })
        "
      >
        <RichText
          :class="$b('copy')"
          size="h4"
          :content="parseText(resolvedCopy)"
        />
      </div>

      <div :class="$b('cta-wrapper')">
        <component
          :is="cta.gradient ? 'PrimaryButton' : 'TertiaryButton'"
          v-if="cta.active"
          :to="cta.link"
          @mouseover="hasHoverOn"
          @mouseleave="hasHoverOff"
        >
          {{ $tr(cta.copy) }}
        </component>
      </div>
      <div
        v-if="features.length"
        :class="$b('features', { fix: !hasCopy })"
      >
        <div
          v-for="(feature, featureIndex) in features"
          :key="feature.id"
          :class="
            $b('feature-container', {
              active: activeIndex === featureIndex,
              out:
                feature.length > 1 &&
                (activeIndex === featureIndex + 1 ||
                  (featureIndex + 1 === feature.length && activeIndex === 0)),
            })
          "
        >
          <img
            v-if="feature.icon"
            :class="$b('feature-icon')"
            :src="feature.icon?.url"
            loading="lazy"
          />
          <RichText
            :class="$b('feature-text')"
            :content="parseText(feature.text)"
          />
        </div>
      </div>
    </div>
  </BaseBanner>
</template>

<script>
import headingMixin from '~/mixins/headingMixin'
import parseTextMixin from '~/mixins/parseTextMixin'
import { PrimaryButton, TertiaryButton } from '#components'

export default {
  name: 'BannerRow',

  components: {
    PrimaryButton,
    TertiaryButton
  },

  mixins: [headingMixin, parseTextMixin],

  inheritAttrs: false,

  props: {
    cta: {
      type: Object,
      default: () => {},
    },

    copy: {
      type: String,
      default: '',
    },

    useRichText: {
      type: Boolean,
      default: false,
    },

    richTextCopy: {
      type: String,
      default: '',
    },

    primaryColour: {
      type: String,
      default: 'white',
      validator: (value) => {
        return ['white', 'black'].includes(value)
      },
    },

    headingGradient: {
      type: String,
      default: 'none',
      validator: (value) => {
        return ['none', 'blue', 'gold'].includes(value)
      },
    },

    maxHeadingWidth: {
      type: Number,
      default: null,
    },

    halfWidthCopy: {
      type: Boolean,
      default: false,
    },

    fullWidthCopy: {
      type: Boolean,
      default: false,
    },

    inlineCopy: {
      type: Boolean,
      default: false,
    },

    features: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      hasHover: false,
      featuresInterval: null,
      activeIndex: 0,
    }
  },

  computed: {
    isSmallHeading() {
      return ['h3', 'h4', 'h5'].includes(this.headingSize)
    },

    hasFeatures() {
      return this.features.length > 0
    },

    resolvedCopy() {
      return this.useRichText ? this.richTextCopy : this.copy
    },

    hasCopy() {
      return this.copy.length > 0 || this.richTextCopy.length > 0
    },
  },

  watch: {
    features(newValue, oldValue) {
      if (newValue.length > 1 && oldValue.length === 1) {
        this.setupTickerInterval()
      }
    },
  },

  mounted() {
    this.setupTickerInterval()
  },

  beforeUnmount() {
    if (this.features.length > 1) {
      clearInterval(this.featuresInterval)
    }
  },

  methods: {
    hasHoverOn() {
      this.hasHover = true
    },

    hasHoverOff() {
      this.hasHover = false
    },

    setupTickerInterval() {
      if (this.features.length <= 1) return

      if (this.featuresInterval) {
        clearInterval(this.featuresInterval)
      }

      this.featuresInterval = setInterval(() => {
        if (this.activeIndex >= this.features.length - 1) {
          this.activeIndex = 0
        } else {
          this.activeIndex += 1
        }
      }, 5000)
    },
  },
}
</script>

<style lang="postcss">
.BannerRow__heading:not(:last-child) {
  @apply mb-sm;
}

.BannerRow__copy-wrapper {
  &--half-width {
    @apply w-1/2;
  }

  &:not(:last-child) {
    @apply mb-md md:mb-lg;
  }

  .BannerRow__copy a {
    @apply text-inherit underline;
  }
}

.BannerRow__heading.t-h1-lg {
  @apply xxsMax:text-5xl;
}

.BannerRow__heading,
.BannerRow__copy {
  & b {
    font: inherit;
  }

  p {
    max-width: 640px;
    max-width: 60ch;

    .Banner__copy-wrapper--full-width & {
      @apply max-w-full;
    }
  }
}

.BannerRow--inline-copy {
  .BannerRow__heading,
  .BannerRow__inline-copy {
    @apply inline;
  }

  .BannerRow__heading {
    @apply pr-xxxs;
  }

  .BannerRow__inline-copy {
    @apply opacity-60;
  }

  .BannerRow__content {
    @apply md:w-3/5;
  }

  .BannerRow__cta {
    @apply block;
  }
}

.BannerRow__heading--wrap {
  @screen lg {
    max-width: 40%;
  }
}

.BannerRow__cta-wrapper {
  @apply mt-md;
}

.BannerRow--colour-black {
  @apply text-off-black;
}

.BannerRow--colour-white {
  @apply text-white;
}

.BannerRow__heading {
  width: fit-content;

  &--gradient-blue {
    background-image: var(--gradient-default);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  &--gradient-gold {
    background-image: var(--gradient-gold);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
}

.BannerRow__features {
  @apply w-full
    mt-lg
    relative;

  @screen md {
    @apply flex max-w-3xl;
  }

  @screen xl {
    @apply max-w-4xl;
  }

  &--fix {
    @apply md:absolute md:bottom-[8%]
      md:mt-0;
  }
}

.BannerRow__feature-container {
  @apply flex items-center;

  @screen mdMax {
    @apply opacity-0 transition-all duration-500 ease-in-out transform absolute bottom-0 left-0;

    &--active {
      @apply opacity-100 -translate-y-1/2;
    }

    &--out {
      @apply opacity-0 -translate-y-full;
    }
  }

  @screen md {
    @apply w-1/3;
  }
}

.BannerRow__feature-icon {
  @apply w-10 mr-xs;

  @screen xl {
    @apply w-12 mr-sm;
  }
}

.BannerRow__feature-text p {
  @apply text-sm leading-tight;

  @screen xl {
    @apply text-base;
  }
}
</style>
